import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { assessmentQuery, downloadAssessment, generatePlan, type PlanRow } from '../api/dnscAssessment'

/**
 * Exporturile si planul de masuri al autoevaluarii DNSC.
 *
 * Exporturile: instrumentul DNSC al nivelului (.xlsx, cu notele in celulele
 * lui) si foaia "Autoevaluare maturitate" (PDF de semnat). Planul de masuri:
 * cerintele sub tinta, scrise ca constatari in IT Audit (registrul de
 * constatari) - se aduce la zi singur la fiecare salvare; butonul il reface la
 * cerere, fara dubluri.
 */

const CLOSED = ['fixed', 'risk accepted', 'not a finding']

export function AssessmentExports() {
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<'' | 'xlsx' | 'pdf'>('')
  const run = (kind: 'xlsx' | 'pdf') => {
    setError('')
    setBusy(kind)
    downloadAssessment(kind)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'not exported'))
      .finally(() => setBusy(''))
  }

  return (
    <div className="nis2-sa-exports" data-sa-exports>
      <button type="button" className="btn btn-default btn-sm" disabled={busy !== ''} data-sa-export="xlsx" onClick={() => run('xlsx')}>
        <i className="fas fa-file-excel" aria-hidden="true"></i> {busy === 'xlsx' ? 'Writing...' : 'DNSC form (.xlsx)'}
      </button>
      <button type="button" className="btn btn-default btn-sm" disabled={busy !== ''} data-sa-export="pdf" onClick={() => run('pdf')}>
        <i className="fas fa-file-pdf" aria-hidden="true"></i> {busy === 'pdf' ? 'Writing...' : 'Maturity self-assessment (PDF)'}
      </button>
      <span className="nis2-muted nis2-sa-exports-note">
        The .xlsx is the DNSC tool of your level with your answers in its cells; Excel recalculates it when opened. TLP: AMBER - every export is logged.
      </span>
      {error ? (
        <span className="nis2-sa-error" data-sa-export-error>
          {error}
        </span>
      ) : null}
    </div>
  )
}

export function PlanOfMeasures({ ready, rows, canAnswer }: { ready: boolean; rows: PlanRow[]; canAnswer: boolean }) {
  const client = useQueryClient()
  const [note, setNote] = useState('')
  const gen = useMutation({
    mutationFn: generatePlan,
    onSuccess: async (out) => {
      if (!out.ok) {
        setNote(out.error ?? 'the plan could not be generated')
        return
      }
      const changed = (out.opened ?? 0) + (out.updated ?? 0) + (out.reopened ?? 0) + (out.closed ?? 0)
      setNote(
        changed === 0
          ? 'The plan was already up to date.'
          : `${out.opened ?? 0} opened, ${out.updated ?? 0} updated, ${out.reopened ?? 0} reopened, ${out.closed ?? 0} closed.`,
      )
      await client.invalidateQueries({ queryKey: assessmentQuery.queryKey })
    },
    onError: (e) => setNote(e instanceof Error ? e.message : 'the plan could not be generated'),
  })
  const open = rows.filter((r) => !CLOSED.includes(r.state))
  const closed = rows.length - open.length

  return (
    <section className="nis2-sa-planbox" data-sa-planbox>
      <div className="nis2-sa-planbox-head">
        <h5>Plan of measures</h5>
        {canAnswer && ready ? (
          <button type="button" className="btn btn-default btn-xs" disabled={gen.isPending} data-sa-generate onClick={() => gen.mutate()}>
            <i className="fas fa-tasks" aria-hidden="true"></i> Generate plan of measures
          </button>
        ) : null}
        {note ? (
          <span className="nis2-muted" data-sa-plan-note>
            {note}
          </span>
        ) : null}
      </div>
      {!ready ? (
        <p className="nis2-muted">The IT Audit findings table is not on this installation yet, so the plan cannot be written as findings.</p>
      ) : rows.length === 0 ? (
        <p className="nis2-muted" data-sa-plan-empty>
          No finding yet. Every key measure below target, and every answered requirement below the key-measure target, becomes a finding in
          the IT Audit register when an answer is saved{canAnswer ? ' or when you generate the plan' : ''}.
        </p>
      ) : (
        <>
          <p className="nis2-muted">
            {open.length} open{closed > 0 ? `, ${closed} closed` : ''} - the same findings are in IT Audit &gt; Findings register, where their owner and
            due date are moved. A finding closes by itself when the requirement reaches the target.
          </p>
          <table className="table table-condensed nis2-sa-plan-table" data-sa-plan-rows>
            <thead>
              <tr>
                <th>Requirement</th>
                <th>Severity</th>
                <th>Finding</th>
                <th>Owner</th>
                <th>State</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.fingerprint} data-sa-plan-row={r.code} className={CLOSED.includes(r.state) ? 'nis2-sa-plan-closed' : undefined}>
                  <td>
                    <strong>{r.code}</strong>
                  </td>
                  <td>
                    <span className={`nis2-sa-sev nis2-sa-sev-${r.severity.toLowerCase()}`}>{r.severity}</span>
                  </td>
                  <td lang="ro">{r.title}</td>
                  <td>{r.owner || <span className="nis2-muted">-</span>}</td>
                  <td>{r.state}</td>
                  <td className={r.overdue ? 'nis2-sa-warn' : undefined}>
                    {r.due_on}
                    {r.overdue ? ' (overdue)' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}

/**
 * Nota pentru admin: dreptul autoevaluarii nu e dat automat niciunui rol
 * (TLP: AMBER), deci cineva trebuie sa-l dea anume. Adminul il are oricum.
 */
export function AssessmentRights({ read, edit }: { read: string; edit: string }) {
  return (
    <div className="nis2-sa-rights" data-sa-rights>
      <i className="fas fa-user-shield" aria-hidden="true"></i> <strong>For administrators:</strong> the self-assessment shows the gaps of the
      organisation (TLP: AMBER), so no role gets it automatically - not even the roles that see the NIS2 screen. Give it in Settings &gt; Roles,
      right <em>NIS2 - DNSC self-assessment</em>:
      <ul>
        <li>
          <strong>edit</strong> ({edit}) to the people who answer: IT manager, security officer (RSI), compliance / NIS2 officer;
        </li>
        <li>
          <strong>read</strong> ({read}) to the people who review and sign: management, internal audit, DPO.
        </li>
      </ul>
      Technicians and helpdesk roles do not need it. Every export and every uploaded document is logged.
    </div>
  )
}
